/**
 * Slice 19d — owns all editor state for the Library Templates panel and
 * unifies the create vs edit flows. Extracted from useAdsAssistantLibrarySection
 * to keep that orchestrator under file-size hygiene limits.
 *
 * `editingTemplateId` distinguishes the two modes:
 *   - null  → create form (Add template)
 *   - "..." → editor pre-filled with an existing template (Save changes / Cancel)
 *
 * `openTemplateForEdit` populates fields from a row; `resetEditor` clears
 * everything and exits edit mode; `saveEditingTemplate` PATCHes the row.
 */
import { useCallback, useState } from "react";
import {
  updateAdsTemplate,
  type AdsAssistantTemplateRow,
} from "@/lib/adsAssistantClient";

export type LibraryTemplateEditor = ReturnType<typeof useAdsAssistantLibraryTemplateEditor>;

export function useAdsAssistantLibraryTemplateEditor(args: {
  onChanged: () => Promise<void> | void;
  setError: (e: string | null) => void;
  setBanner: (b: string | null) => void;
  onAfterOpen?: () => void;
}) {
  const { onChanged, setError, setBanner, onAfterOpen } = args;

  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [tplName, setTplName] = useState("");
  const [tplCategoryId, setTplCategoryId] = useState("");
  const [tplBody, setTplBody] = useState("");
  const [tplSlotsJson, setTplSlotsJson] = useState("");
  const [tplExamplePostIds, setTplExamplePostIds] = useState<string[]>([]);
  const [linkTplExamples, setLinkTplExamples] = useState(true);
  /** Slice 19e — trainer-written exemplar captions (≤5; server clamps each entry to 1500 chars). */
  const [tplCustomExemplarTexts, setTplCustomExemplarTexts] = useState<string[]>([]);
  const [tplArchetypeTagIds, setTplArchetypeTagIds] = useState<string[]>([]);
  const [tplSuggestedTopicTagIds, setTplSuggestedTopicTagIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const resetEditor = useCallback(() => {
    setEditingTemplateId(null);
    setTplName("");
    setTplCategoryId("");
    setTplBody("");
    setTplSlotsJson("");
    setTplExamplePostIds([]);
    setLinkTplExamples(true);
    setTplCustomExemplarTexts([]);
    setTplArchetypeTagIds([]);
    setTplSuggestedTopicTagIds([]);
  }, []);

  const openTemplateForEdit = useCallback(
    (t: AdsAssistantTemplateRow) => {
      setEditingTemplateId(t.id);
      setTplName(t.name || "");
      setTplCategoryId(t.category_id ?? "");
      setTplBody(t.body_markdown || "");
      setTplSlotsJson(
        Array.isArray(t.slots_schema) && t.slots_schema.length
          ? JSON.stringify(t.slots_schema, null, 2)
          : "",
      );
      setTplExamplePostIds(
        Array.isArray(t.example_social_post_ids) ? t.example_social_post_ids : [],
      );
      setLinkTplExamples(true);
      setTplCustomExemplarTexts(
        Array.isArray(t.custom_exemplar_texts)
          ? t.custom_exemplar_texts.filter((x): x is string => typeof x === "string")
          : [],
      );
      setTplArchetypeTagIds(
        Array.isArray(t.archetype_tag_ids) ? t.archetype_tag_ids : [],
      );
      setTplSuggestedTopicTagIds([]);
      onAfterOpen?.();
    },
    [onAfterOpen],
  );

  const parseSlotsOrError = useCallback((): unknown[] | "error" => {
    const raw = tplSlotsJson.trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) throw new Error("not array");
      return parsed;
    } catch {
      setError("Slots JSON must be an array of { id, label, … } objects.");
      return "error";
    }
  }, [tplSlotsJson, setError]);

  const saveEditingTemplate = useCallback(async () => {
    if (!editingTemplateId) return;
    const slots_schema = parseSlotsOrError();
    if (slots_schema === "error") return;
    setSaving(true);
    setError(null);
    const r = await updateAdsTemplate(editingTemplateId, {
      name: tplName.trim(),
      body_markdown: tplBody,
      category_id: tplCategoryId || null,
      slots_schema,
      example_social_post_ids: linkTplExamples ? tplExamplePostIds : [],
      custom_exemplar_texts: tplCustomExemplarTexts,
      archetype_tag_ids: tplArchetypeTagIds,
    });
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setBanner("Template updated.");
    await onChanged();
    resetEditor();
  }, [
    editingTemplateId,
    parseSlotsOrError,
    tplName,
    tplBody,
    tplCategoryId,
    tplExamplePostIds,
    linkTplExamples,
    tplCustomExemplarTexts,
    tplArchetypeTagIds,
    setError,
    setBanner,
    onChanged,
    resetEditor,
  ]);

  const formHasContent = useCallback(
    () =>
      !!(
        tplName.trim() ||
        tplBody.trim() ||
        tplSlotsJson.trim() ||
        tplArchetypeTagIds.length ||
        tplCustomExemplarTexts.length
      ),
    [tplName, tplBody, tplSlotsJson, tplArchetypeTagIds, tplCustomExemplarTexts],
  );

  return {
    editingTemplateId,
    isEditing: editingTemplateId !== null,
    tplName,
    setTplName,
    tplCategoryId,
    setTplCategoryId,
    tplBody,
    setTplBody,
    tplSlotsJson,
    setTplSlotsJson,
    tplExamplePostIds,
    setTplExamplePostIds,
    linkTplExamples,
    setLinkTplExamples,
    tplCustomExemplarTexts,
    setTplCustomExemplarTexts,
    tplArchetypeTagIds,
    setTplArchetypeTagIds,
    tplSuggestedTopicTagIds,
    setTplSuggestedTopicTagIds,
    saving,
    openTemplateForEdit,
    resetEditor,
    saveEditingTemplate,
    formHasContent,
    parseSlotsOrError,
  };
}
